/**
 * Large features of the hall: the illuminated messages on the walls (one atlas, one draw
 * call, a slow light sweep in the shader) and the two floating displays above the
 * reception: a turning molecule, and a globe of studies, sites and patients joined by arcs
 * that carry pulses of light.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { BODY_FONT, DISPLAY_FONT, TOKENS } from '../../ui/theme.ts';
import { glowMaterial, radialTexture, seeded, type TimeUniform } from './glow.ts';

const DEG = Math.PI / 180;

/** The messages: purpose, not claims. */
export const MESSAGES = [
  {
    eyebrow: 'Why we are here',
    headline: 'Every data point is a patient.',
    line: 'Review with care. Query with precision.',
    angle: -38,
    y: 6.4,
  },
  {
    eyebrow: 'Clean data, sooner',
    headline: 'Faster answers for the people waiting.',
    line: 'Every resolved query moves a study forward.',
    angle: 38,
    y: 6.4,
  },
  {
    eyebrow: 'Together',
    headline: 'Advancing medicine as one team.',
    line: 'Sites, sponsors and reviewers in one shared space.',
    angle: 180,
    y: 7.6,
  },
];

export function buildMessages(time: TimeUniform, radius: number): THREE.Mesh {
  const W = 2048;
  const rowH = 512;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = rowH * MESSAGES.length;
  const g = canvas.getContext('2d')!;
  MESSAGES.forEach((m, i) => {
    const y0 = i * rowH;
    const bg = g.createLinearGradient(0, y0, 0, y0 + rowH);
    bg.addColorStop(0, 'rgba(22,30,42,0.82)');
    bg.addColorStop(1, 'rgba(10,14,20,0.82)');
    g.fillStyle = bg;
    g.beginPath();
    g.roundRect(8, y0 + 8, W - 16, rowH - 16, 36);
    g.fill();
    const edge = g.createLinearGradient(0, 0, W, 0);
    edge.addColorStop(0, 'rgba(63,214,255,0)');
    edge.addColorStop(0.5, 'rgba(63,214,255,0.9)');
    edge.addColorStop(1, 'rgba(63,214,255,0)');
    g.fillStyle = edge;
    g.fillRect(200, y0 + 9, W - 400, 3);
    g.textAlign = 'center';
    g.textBaseline = 'alphabetic';
    g.fillStyle = TOKENS.cyanSoft;
    g.font = `600 44px ${BODY_FONT}`;
    g.letterSpacing = '10px';
    g.fillText(m.eyebrow.toUpperCase(), W / 2, y0 + 130);
    g.letterSpacing = '0px';
    g.fillStyle = TOKENS.white;
    g.font = `600 112px ${DISPLAY_FONT}`;
    g.fillText(m.headline, W / 2, y0 + 290, W - 120);
    g.fillStyle = TOKENS.silver;
    g.font = `400 58px ${BODY_FONT}`;
    g.fillText(m.line, W / 2, y0 + 400, W - 160);
  });
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  const width = 7.2;
  const height = width / (W / rowH);
  const parts = MESSAGES.map((m, i) => {
    const plane = new THREE.PlaneGeometry(width, height);
    const uv = plane.attributes.uv!;
    for (let k = 0; k < uv.count; k++) {
      const v = uv.getY(k);
      uv.setY(k, 1 - (i + 1 - v) / MESSAGES.length);
    }
    const phi = m.angle * DEG;
    // A flat panel on a curved wall: stand it off by more than the curve's depth.
    const r = radius - 0.7;
    plane.rotateY(-phi);
    plane.translate(Math.sin(phi) * r, m.y, -Math.cos(phi) * r);
    return plane;
  });
  const material = new THREE.ShaderMaterial({
    uniforms: { uTime: time, uMap: { value: texture }, uRows: { value: MESSAGES.length } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform sampler2D uMap;
      uniform float uRows;
      varying vec2 vUv;
      void main() {
        vec4 c = texture2D(uMap, vUv);
        float row = floor((1.0 - vUv.y) * uRows);
        float s = fract(uTime * 0.035 + row * 0.37);
        float sweep = exp(-pow((vUv.x - (s * 1.6 - 0.3)) * 7.0, 2.0));
        c.rgb *= 0.9 + sweep * 0.55;
        c.rgb += vec3(0.25, 0.8, 1.0) * sweep * 0.05 * c.a;
        gl_FragColor = c;
        #include <colorspace_fragment>
      }`,
    transparent: true,
    depthWrite: false,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(mergeGeometries(parts)!, material);
  mesh.name = 'lobby-messages';
  return mesh;
}

export interface Displays {
  group: THREE.Group;
  update(dt: number): void;
}

/** The two floating displays above the reception, facing the arrival point. */
export const DISPLAYS = [
  { x: -5.4, y: 5.1, z: 4.4, caption: 'Molecular science' },
  { x: 5.4, y: 5.1, z: 4.4, caption: 'Studies · sites · patients' },
];

export function buildDisplays(time: TimeUniform, facing: THREE.Vector3): Displays {
  const group = new THREE.Group();
  group.name = 'displays';
  const w = 3.4;
  const h = 2.2;
  const frames: THREE.BufferGeometry[] = [];
  const outlines: THREE.Vector3[] = [];
  const captions: THREE.BufferGeometry[] = [];
  const captionCanvas = document.createElement('canvas');
  captionCanvas.width = 1024;
  captionCanvas.height = 128 * DISPLAYS.length;
  const cg = captionCanvas.getContext('2d')!;
  const holders = DISPLAYS.map((d, i) => {
    const yaw = Math.atan2(facing.x - d.x, facing.z - d.z);
    const m = new THREE.Matrix4().compose(
      new THREE.Vector3(d.x, d.y, d.z),
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw),
      new THREE.Vector3(1, 1, 1),
    );
    frames.push(new THREE.PlaneGeometry(w, h).applyMatrix4(m));
    // Rounded outline as a loop of segments.
    const shape = new THREE.Shape();
    const r = 0.18;
    shape.moveTo(-w / 2 + r, -h / 2);
    shape.lineTo(w / 2 - r, -h / 2);
    shape.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + r);
    shape.lineTo(w / 2, h / 2 - r);
    shape.quadraticCurveTo(w / 2, h / 2, w / 2 - r, h / 2);
    shape.lineTo(-w / 2 + r, h / 2);
    shape.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - r);
    shape.lineTo(-w / 2, -h / 2 + r);
    shape.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + r, -h / 2);
    const pts = shape.getPoints(10).map((p) => new THREE.Vector3(p.x, p.y, 0.002).applyMatrix4(m));
    for (let k = 0; k < pts.length; k++) outlines.push(pts[k]!, pts[(k + 1) % pts.length]!);
    // Caption under the display.
    cg.fillStyle = TOKENS.silver;
    cg.font = `500 46px ${BODY_FONT}`;
    cg.letterSpacing = '8px';
    cg.textAlign = 'center';
    cg.textBaseline = 'middle';
    cg.fillText(d.caption.toUpperCase(), 512, i * 128 + 64);
    const cap = new THREE.PlaneGeometry(2.4, 0.3);
    const uv = cap.attributes.uv!;
    for (let k = 0; k < uv.count; k++) uv.setY(k, 1 - (i + 1 - uv.getY(k)) / DISPLAYS.length);
    cap.translate(0, -h / 2 - 0.28, 0).applyMatrix4(m);
    captions.push(cap);
    const holder = new THREE.Group();
    holder.position.set(d.x, d.y, d.z);
    holder.rotation.y = yaw;
    group.add(holder);
    return holder;
  });
  const glass = new THREE.Mesh(
    mergeGeometries(frames)!,
    new THREE.MeshBasicMaterial({
      color: '#0b1520',
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      side: THREE.DoubleSide,
      // Additive or see-through: one pass, not back faces then front faces.
      forceSinglePass: true,
      fog: false,
    }),
  );
  group.add(glass);
  const outline = new THREE.LineSegments(
    new THREE.BufferGeometry().setFromPoints(outlines),
    new THREE.LineBasicMaterial({
      color: TOKENS.cyan,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  group.add(outline);
  const captionTexture = new THREE.CanvasTexture(captionCanvas);
  captionTexture.colorSpace = THREE.SRGBColorSpace;
  const caption = new THREE.Mesh(
    mergeGeometries(captions)!,
    new THREE.MeshBasicMaterial({
      map: captionTexture,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    }),
  );
  group.add(caption);

  const molecule = buildMolecule();
  molecule.position.z = 0.35;
  holders[0]!.add(molecule);
  const globe = buildGlobe(time);
  globe.group.position.z = 0.35;
  holders[1]!.add(globe.group);

  return {
    group,
    update: (dt) => {
      molecule.rotation.y += dt * 0.25;
      molecule.rotation.x = Math.sin(time.value * 0.15) * 0.35;
      globe.group.rotation.y += dt * 0.12;
    },
  };
}

/** An abstract ball-and-stick molecule: two rings and a chain, about twenty atoms. */
function buildMolecule(): THREE.Group {
  const group = new THREE.Group();
  const atoms: { p: THREE.Vector3; kind: number }[] = [];
  const bonds: [number, number][] = [];
  const ring = (cx: number, cy: number, r: number, n: number, start: number): number[] => {
    const ids: number[] = [];
    for (let i = 0; i < n; i++) {
      const a = start + (i / n) * Math.PI * 2;
      atoms.push({
        p: new THREE.Vector3(cx + Math.cos(a) * r, cy + Math.sin(a) * r, (i % 2) * 0.12 - 0.06),
        kind: i % 3 === 0 ? 1 : 0,
      });
      ids.push(atoms.length - 1);
    }
    ids.forEach((id, i) => bonds.push([id, ids[(i + 1) % n]!]));
    return ids;
  };
  const a = ring(-0.42, 0.05, 0.36, 6, 0);
  const b = ring(0.3, -0.12, 0.32, 5, Math.PI / 5);
  bonds.push([a[0]!, b[2]!]);
  let prev = b[0]!;
  for (let i = 0; i < 4; i++) {
    atoms.push({
      p: new THREE.Vector3(0.62 + i * 0.22, 0.25 + (i % 2) * 0.2, 0.05),
      kind: i === 3 ? 2 : 0,
    });
    bonds.push([prev, atoms.length - 1]);
    prev = atoms.length - 1;
  }
  for (const id of [a[2]!, a[4]!]) {
    const p = atoms[id]!.p;
    atoms.push({
      p: p
        .clone()
        .multiplyScalar(1.5)
        .add(new THREE.Vector3(-0.2, 0, 0.1)),
      kind: 2,
    });
    bonds.push([id, atoms.length - 1]);
  }
  const centre = new THREE.Vector3();
  for (const at of atoms) centre.add(at.p);
  centre.divideScalar(atoms.length);
  const colours = [TOKENS.cyanSoft, TOKENS.white, TOKENS.blue];
  const spheres = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.05, 16, 12),
    glowMaterial('#ffffff', 1),
    atoms.length,
  );
  const d = new THREE.Object3D();
  const c = new THREE.Color();
  atoms.forEach((at, i) => {
    at.p.sub(centre);
    d.position.copy(at.p);
    d.scale.setScalar(at.kind === 1 ? 1.25 : at.kind === 2 ? 0.8 : 1);
    d.updateMatrix();
    spheres.setMatrixAt(i, d.matrix);
    spheres.setColorAt(i, c.set(colours[at.kind]!));
  });
  const sticks = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0.014, 0.014, 1, 6),
    glowMaterial(TOKENS.cyan, 0.75),
    bonds.length,
  );
  const up = new THREE.Vector3(0, 1, 0);
  bonds.forEach(([i, j], k) => {
    const p = atoms[i]!.p;
    const q = atoms[j]!.p;
    d.position.copy(p).add(q).multiplyScalar(0.5);
    d.quaternion.setFromUnitVectors(up, q.clone().sub(p).normalize());
    d.scale.set(1, p.distanceTo(q), 1);
    d.updateMatrix();
    sticks.setMatrixAt(k, d.matrix);
  });
  group.add(spheres, sticks);
  group.scale.setScalar(1.05);
  return group;
}

/** A globe of light points with hub nodes linked by arcs; pulses run along the arcs. */
function buildGlobe(time: TimeUniform): { group: THREE.Group } {
  const group = new THREE.Group();
  const r = 0.82;
  const count = 900;
  const pos = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const rr = Math.sqrt(1 - y * y);
    const t = i * 2.39996;
    pos.set([Math.cos(t) * rr * r, y * r, Math.sin(t) * rr * r], i * 3);
  }
  const dotsGeometry = new THREE.BufferGeometry();
  dotsGeometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const dots = new THREE.Points(
    dotsGeometry,
    new THREE.PointsMaterial({
      color: TOKENS.cyan,
      size: 0.04,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
      map: radialTexture(),
    }),
  );
  group.add(dots);
  const rand = seeded(88);
  const hubs = Array.from({ length: 11 }, () => {
    const u = rand() * 2 - 1;
    const t = rand() * Math.PI * 2;
    const rr = Math.sqrt(1 - u * u);
    return new THREE.Vector3(Math.cos(t) * rr, u, Math.sin(t) * rr).multiplyScalar(r);
  });
  const hubMesh = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.035, 10, 8),
    glowMaterial(TOKENS.cyanSoft, 1),
    hubs.length,
  );
  const d = new THREE.Object3D();
  hubs.forEach((h, i) => {
    d.position.copy(h);
    d.updateMatrix();
    hubMesh.setMatrixAt(i, d.matrix);
  });
  group.add(hubMesh);
  // Arcs between hubs, lifted off the surface, with an along-arc parameter for pulses.
  const positions: number[] = [];
  const along: number[] = [];
  for (let i = 0; i < hubs.length; i++)
    for (const j of [(i + 1) % hubs.length, (i + 4) % hubs.length]) {
      const a = hubs[i]!;
      const b = hubs[j]!;
      const steps = 24;
      for (let s = 0; s < steps; s++)
        for (const k of [s, s + 1]) {
          const t = k / steps;
          const p = a
            .clone()
            .lerp(b, t)
            .normalize()
            .multiplyScalar(r * (1 + Math.sin(t * Math.PI) * 0.28));
          positions.push(p.x, p.y, p.z);
          along.push(t + i * 0.13);
        }
    }
  const arcGeometry = new THREE.BufferGeometry();
  arcGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  arcGeometry.setAttribute('aAlong', new THREE.Float32BufferAttribute(along, 1));
  const arcs = new THREE.LineSegments(
    arcGeometry,
    new THREE.ShaderMaterial({
      uniforms: { uTime: time, uColour: { value: new THREE.Color(TOKENS.cyan) } },
      vertexShader: /* glsl */ `
        attribute float aAlong;
        varying float vAlong;
        void main() {
          vAlong = aAlong;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uColour;
        varying float vAlong;
        void main() {
          float x = fract(vAlong - uTime * 0.25);
          float pulse = pow(1.0 - x, 18.0);
          float a = 0.22 + pulse * 0.9;
          gl_FragColor = vec4(mix(uColour, vec3(1.0), pulse) * a, a);
        }`,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false,
    }),
  );
  group.add(arcs);
  return { group };
}
